import { Button, Form, Input, Radio, Flex, Modal, Switch, Table, Space, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";
import ModalWrapper from "../../components/generic/modal";
import { useState } from "react";
import {
  ConfigureSmsWebhookPayload,
  SmsWebhook,
  useConfigureSmsWebhook,
  useDeleteSmsWebhook,
  useListSmsWebhooks,
  useTestSmsWebhook,
  useUpdateSmsWebhook,
} from "../../hooks/apis/integrations.api";
import { useMessageToast } from "../../components/Layout/DefaultLayout";
import { usePermissions } from "../../hooks/permissions";
import "./integration.css";

const httpsUrlRule = {
  validator: (_: unknown, value?: string) => {
    if (!value || value.startsWith("https://")) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Webhook URLs must use HTTPS"));
  },
};

const { confirm } = Modal;

const WebhookIntegration = () => {
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<SmsWebhook | null>(null);
  const [form] = Form.useForm<ConfigureSmsWebhookPayload>();
  const authType = Form.useWatch(["auth", "type"], form) ?? "none";
  const { messageApi } = useMessageToast();
  const { hasPermission2 } = usePermissions();

  const canViewWebhook = hasPermission2("sms.webhooks.view", "account");
  const canCreateWebhook = hasPermission2("sms.webhooks.create", "account");
  const canUpdateWebhook = hasPermission2("sms.webhooks.update", "account");
  const canDeleteWebhook = hasPermission2("sms.webhooks.delete", "account");
  const canTestWebhook = hasPermission2("sms.webhooks.test", "account");
  const canConfigureWebhook = canCreateWebhook || canUpdateWebhook;

  const webhooksQuery = useListSmsWebhooks(canViewWebhook);

  const closeModal = () => {
    setIsWebhookModalOpen(false);
    setEditingWebhook(null);
    form.resetFields();
  };

  const { mutate: createWebhook, isLoading: isCreating } = useConfigureSmsWebhook({
    onSuccess: () => {
      messageApi.success("SMS webhook saved successfully");
      closeModal();
    },
    onError: () => messageApi.error("Failed to configure SMS webhook"),
  });

  const { mutate: updateWebhook, isLoading: isUpdating } = useUpdateSmsWebhook({
    onSuccess: () => {
      messageApi.success("SMS webhook saved successfully");
      closeModal();
    },
    onError: () => messageApi.error("Failed to save SMS webhook"),
  });

  const { mutate: deleteWebhook, isLoading: isDeleting } = useDeleteSmsWebhook({
    onSuccess: () => messageApi.success("SMS webhook deleted"),
    onError: () => messageApi.error("Failed to delete SMS webhook"),
  });

  const { mutate: testWebhook, isLoading: isTesting } = useTestSmsWebhook({
    onSuccess: () => messageApi.success("Test webhook delivery queued"),
    onError: () => messageApi.error("Failed to queue test delivery"),
  });

  const showDeleteConfirm = (webhook: SmsWebhook) => {
    confirm({
      title: "Are you sure you want to delete this webhook?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => deleteWebhook(webhook.id),
    });
  };

  const openCreateWebhook = () => {
    setEditingWebhook(null);
    form.resetFields();
    form.setFieldsValue({
      direction: "both",
      enabled: false,
      auth: { type: "none" },
      payload_version: 2,
    });
    setIsWebhookModalOpen(true);
  };

  const openEditWebhook = (webhook: SmsWebhook) => {
    setEditingWebhook(webhook);
    form.setFieldsValue({
      ...webhook,
      auth: { type: webhook.auth_type },
    });
    setIsWebhookModalOpen(true);
  };

  const handleSaveWebhook = (values: ConfigureSmsWebhookPayload) => {
    if (!canConfigureWebhook) return;

    const payload: ConfigureSmsWebhookPayload = {
      ...values,
      failover_url: values.failover_url || null,
      did: values.did || null,
    };

    if (
      editingWebhook &&
      values.auth?.type === editingWebhook.auth_type &&
      values.auth.type !== "none" &&
      !(values.auth.type === "bearer" && "token" in values.auth && values.auth.token) &&
      !(values.auth.type === "basic" && "password" in values.auth && values.auth.password)
    ) {
      delete payload.auth;
    }

    if (editingWebhook) {
      updateWebhook({ id: editingWebhook.id, values: payload });
    } else {
      createWebhook(payload);
    }
  };

  return (
    <div style={{ padding: 20, width: "100%" }}>
      <Flex justify="space-between" align="center" gap={16} wrap>
        <Typography.Title level={2} style={{ margin: 0 }}>
          SMS Webhooks
        </Typography.Title>
        {canCreateWebhook && (
          <Button type="primary" onClick={openCreateWebhook}>
            Add Webhook
          </Button>
        )}
      </Flex>

      {canViewWebhook && (
        <div className="webhook-list" style={{ width: "100%", marginTop: 20, background: "#fff", borderRadius: 10, padding: 16 }}>
          <Table<SmsWebhook>
            loading={webhooksQuery.isLoading}
            dataSource={webhooksQuery.data ?? []}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: "Label",
                dataIndex: "label",
                render: (value: string | null) => value || "SMS webhook",
              },
              { title: "Endpoint", dataIndex: "url", ellipsis: true },
              { title: "Events", dataIndex: "direction" },
              {
                title: "Enabled",
                dataIndex: "enabled",
                render: (value: boolean) => (value ? "Yes" : "No"),
              },
              { title: "Auth", dataIndex: "auth_type" },
              {
                title: "Actions",
                render: (_, webhook) => (
                  <Space size="small" wrap>
                    {canTestWebhook && (
                      <Button size="small" loading={isTesting} onClick={() => testWebhook(webhook.id)}>
                        Test
                      </Button>
                    )}
                    {canUpdateWebhook && (
                      <CiEdit
                        aria-label="Edit webhook"
                        style={{ cursor: "pointer" }}
                        onClick={() => openEditWebhook(webhook)}
                      />
                    )}
                    {canDeleteWebhook && (
                      <MdDeleteOutline
                        aria-label="Delete webhook"
                        style={{ cursor: isDeleting ? "wait" : "pointer" }}
                        onClick={() => {
                          if (!isDeleting) showDeleteConfirm(webhook);
                        }}
                      />
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </div>
      )}

      {isWebhookModalOpen && (
        <ModalWrapper
          variant="sec"
          title={editingWebhook ? "Edit Webhook Integration" : "Add Webhook Integration"}
          isModalOpen={isWebhookModalOpen}
          setIsModalOpen={(open: boolean) => {
            if (!open) closeModal();
            else setIsWebhookModalOpen(true);
          }}
        >
          <div className="webhook-modal">
            <Form
              layout="vertical"
              form={form}
              onFinish={handleSaveWebhook}
              initialValues={{ direction: "both", enabled: false, auth: { type: "none" }, payload_version: 2 }}
            >
              <Form.Item label="Label" name="label">
                <Input size="large" placeholder="CRM notification" allowClear />
              </Form.Item>
              <Form.Item
                label="URL"
                name="url"
                rules={[
                  { required: true, message: "Please enter URL" },
                  { type: "url", message: "Enter a valid URL" },
                  httpsUrlRule,
                ]}
              >
                <Input size="large" placeholder="https://example.com/webhook" allowClear />
              </Form.Item>
              <Form.Item
                label="Failover URL"
                name="failover_url"
                rules={[{ type: "url", message: "Enter a valid URL" }, httpsUrlRule]}
              >
                <Input size="large" placeholder="https://backup.example.com/webhook" allowClear />
              </Form.Item>
              <Form.Item label="Enabled" name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item label="Payload version" name="payload_version">
                <Radio.Group className="webhook-radio-row">
                  <Radio value={2}>v2 (recommended)</Radio>
                  <Radio value={1}>v1 (legacy)</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item label="Authentication" name={["auth", "type"]} initialValue="none">
                <Radio.Group className="webhook-radio-row">
                  <Radio value="none">None</Radio>
                  <Radio value="bearer">Bearer</Radio>
                  <Radio value="basic">Basic</Radio>
                </Radio.Group>
              </Form.Item>
              {authType === "bearer" && (
                <Form.Item
                  label="Bearer token"
                  name={["auth", "token"]}
                  rules={[{ required: !editingWebhook, message: "Enter a bearer token" }]}
                >
                  <Input.Password
                    size="large"
                    autoComplete="new-password"
                    placeholder={editingWebhook ? "Leave blank to keep existing token" : undefined}
                  />
                </Form.Item>
              )}
              {authType === "basic" && (
                <>
                  <Form.Item
                    label="Username"
                    name={["auth", "username"]}
                    rules={[{ required: !editingWebhook, message: "Enter a username" }]}
                  >
                    <Input size="large" autoComplete="username" />
                  </Form.Item>
                  <Form.Item
                    label="Password"
                    name={["auth", "password"]}
                    rules={[{ required: !editingWebhook, message: "Enter a password" }]}
                  >
                    <Input.Password
                      size="large"
                      autoComplete="new-password"
                      placeholder={editingWebhook ? "Leave blank to keep existing password" : undefined}
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item
                label="Which SMS to receive"
                name="direction"
                rules={[{ required: true, message: "Select at least one option" }]}
              >
                <Radio.Group className="webhook-radio-row" style={{ width: "100%" }}>
                  <Radio value="inbound">Inbound</Radio>
                  <Radio value="outbound">Outbound</Radio>
                  <Radio value="both">Both</Radio>
                </Radio.Group>
              </Form.Item>
              <Flex style={{ marginTop: 38 }} className="webhook-actions" justify="start" gap={12}>
                <Button type="primary" size="large" htmlType="submit" loading={isCreating || isUpdating}>
                  {editingWebhook ? "Save Webhook" : "Add Webhook"}
                </Button>
                <Button size="large" onClick={closeModal}>
                  Cancel
                </Button>
              </Flex>
            </Form>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

export default WebhookIntegration;
